import { useState, useEffect, useMemo, useCallback } from 'react';
import { DEFAULT_PAGE_SIZE, getPaginationMeta } from '../utils/paginationUtils';

/**
 * Client-side pagination for in-memory lists (no route changes / refetch).
 * Resets to page 0 when resetKey changes (e.g. filter query).
 *
 * @template T
 * @param {T[]} items
 * @param {number} [pageSize]
 * @param {unknown} [resetKey] - when this changes, page resets to 0
 */
export function useClientPagination(items, pageSize = DEFAULT_PAGE_SIZE, resetKey) {
  const [page, setPage] = useState(0);
  const list = useMemo(
    () => (Array.isArray(items) ? items : []),
    [items]
  );
  const count = list.length;

  useEffect(() => {
    setPage(0);
  }, [resetKey]);

  const meta = useMemo(
    () => getPaginationMeta({ count, page, pageSize }),
    [count, page, pageSize]
  );

  useEffect(() => {
    if (page > meta.totalPages - 1) {
      setPage(Math.max(0, meta.totalPages - 1));
    }
  }, [page, meta.totalPages]);

  const pageItems = useMemo(
    () => list.slice(meta.offset, meta.offset + meta.limit),
    [list, meta.offset, meta.limit]
  );

  const onPrev = useCallback(() => {
    setPage((p) => Math.max(0, p - 1));
  }, []);

  const onNext = useCallback(() => {
    setPage((p) => Math.min(meta.totalPages - 1, p + 1));
  }, [meta.totalPages]);

  const goToPage = useCallback(
    (nextPage) => {
      setPage(Math.min(Math.max(0, nextPage), meta.totalPages - 1));
    },
    [meta.totalPages]
  );

  return {
    items: pageItems,
    ...meta,
    onPrev,
    onNext,
    goToPage,
    setPage,
  };
}
